import { useEffect, useState } from "react";
import { DatabaseBackup, Plus, Trash2, RotateCcw, Download, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { backups, api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function Backups() {
  const [list, setList] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [wizard, setWizard] = useState(null); // {backup, current}
  const [confirmText, setConfirmText] = useState("");

  const load = async () => { try { setList(await backups.list()); } catch (e) { toast.error(apiError(e)); setList([]); } };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try { await backups.create(name || undefined); toast.success("Backup creato"); setName(""); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const download = async (b) => {
    try {
      const full = await backups.get(b.id);
      const blob = new Blob([JSON.stringify(full, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = url; a.download = `${(b.name || "backup").replace(/\s+/g, "_")}.json`; a.click(); URL.revokeObjectURL(url);
      toast.success("Backup scaricato");
    } catch (e) { toast.error(apiError(e)); }
  };
  const openWizard = async (b) => {
    try {
      const [full, ov] = await Promise.all([backups.get(b.id), api.overview()]);
      setWizard({ backup: full, current: ov }); setConfirmText("");
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (b) => { await backups.remove(b.id); toast.success("Backup eliminato"); load(); };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Backup</h1>
        <p className="text-sm text-slate-400">Copie persistenti della struttura del server. Conservazione: ultimi 90.</p>
      </div>

      <div className="card-elevated flex flex-col gap-3 p-5 sm:flex-row sm:items-end">
        <div className="flex-1"><label className="mb-1 block text-xs font-bold uppercase text-slate-500">Nome backup</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Es: Pre-Manutenzione" data-testid="backup-name-input" className="rounded-xl border-white/10 bg-[#0B0C10] text-white" /></div>
        <Button onClick={create} disabled={busy} data-testid="backup-create-button" className="bg-[#5865F2] hover:bg-[#4752C4]">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}Crea backup</Button>
      </div>

      <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#12141D] px-4 py-3 text-xs text-slate-400">
        <Info className="h-4 w-4 text-[#5865F2]" /> Backup automatici giornalieri attivi (03:00). Snapshot pre-operazione creati automaticamente prima delle operazioni massive.
      </div>

      <div className="card-elevated overflow-hidden" data-testid="backups-list">
        {list === null && <div className="space-y-2 p-4">{[0,1,2].map(i=><Skeleton key={i} className="h-16 rounded-lg bg-white/5" />)}</div>}
        {list && list.length === 0 && <div className="p-10 text-center text-sm text-slate-500">Nessun backup. Creane uno o attendi quello automatico.</div>}
        {list && list.length > 0 && (
          <div className="divide-y divide-white/5">
            {list.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-white/5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#5865F2]/15"><DatabaseBackup className="h-5 w-5 text-[#8ea0ff]" /></div>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 font-semibold text-white">{b.name}<span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${b.auto ? "bg-[#5865F2] text-white" : "bg-white/10 text-slate-300"}`}>{b.auto ? "AUTO" : "MANUALE"}</span></p>
                  <p className="text-xs text-slate-500">{new Date(b.at).toLocaleString("it-IT")} · {b.roleCount} ruoli · {b.channelCount} canali · {b.memberCount} membri</p>
                </div>
                <Button size="sm" onClick={() => openWizard(b)} data-testid={`backup-restore-${b.id}`} className="bg-[#248046] hover:bg-[#1a6334]"><RotateCcw className="mr-1 h-4 w-4" />Ripristina</Button>
                <Button size="icon" variant="ghost" onClick={() => download(b)} className="text-slate-300 hover:text-white"><Download className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(b)} className="text-[#F23F43]"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* wizard restore */}
      <Dialog open={!!wizard} onOpenChange={(o) => !o && setWizard(null)}>
        <DialogContent className="max-w-lg border-white/10 bg-[#12141D] text-slate-200">
          <DialogHeader><DialogTitle className="text-white">Ripristino backup — anteprima</DialogTitle></DialogHeader>
          {wizard && (() => {
            const bs = wizard.backup.snapshot || {}; const cur = wizard.current;
            const bRoles = (bs.roles || []).length, bCh = (bs.channels || []).length;
            const cRoles = cur.roles.total, cCh = cur.channels.total + cur.categories.total;
            return (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl border border-white/10 bg-[#0B0C10] p-3"><p className="mb-1 text-xs font-bold uppercase text-slate-500">Backup</p><p className="text-white">{bRoles} ruoli</p><p className="text-white">{bCh} canali</p></div>
                  <div className="rounded-xl border border-white/10 bg-[#0B0C10] p-3"><p className="mb-1 text-xs font-bold uppercase text-slate-500">Server attuale</p><p className="text-white">{cRoles} ruoli</p><p className="text-white">{cCh} canali</p></div>
                </div>
                <div className="rounded-xl border border-[#5865F2]/30 bg-[#5865F2]/10 p-3 text-sm">
                  <p className="font-semibold text-white">Differenze rilevate</p>
                  <p className="text-slate-300">Ruoli: {cRoles - bRoles >= 0 ? "+" : ""}{cRoles - bRoles} · Canali: {cCh - bCh >= 0 ? "+" : ""}{cCh - bCh}</p>
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-[#FEE75C]/20 bg-[#FEE75C]/10 p-3 text-xs text-[#FEE75C]">
                  <Info className="h-4 w-4 shrink-0" /> L'applicazione reale del ripristino a Discord (creazione/modifica/eliminazione di ruoli e canali) richiede il bot collegato e verrà eseguita tramite job con avanzamento. Per confermare, scrivi <b>RIPRISTINA</b>.
                </div>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Scrivi RIPRISTINA" data-testid="restore-confirm-input" className="rounded-xl border-white/10 bg-[#0B0C10] text-white" />
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWizard(null)} className="text-slate-400">Annulla</Button>
            <Button disabled={confirmText !== "RIPRISTINA"} data-testid="restore-confirm-button"
              onClick={() => { toast.success("Ripristino accodato — verrà eseguito dal bot quando collegato"); setWizard(null); }}
              className="bg-[#248046] hover:bg-[#1a6334]"><RotateCcw className="mr-2 h-4 w-4" />Conferma ripristino</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
